import { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { BrowserProductCards } from "../../components/Product/BrowserProductCards";
import type { ICartItem, ICategory, IProduct } from "../../store/types";
import { fetchProductsByCategory } from "../../store/product";
import { addOrderItem, updateOrderModifier } from "../../store/order";
import { genHash, itemsSelector } from "../../store/selector/cart";
import { catSelector } from "../../store/selector/category";
import type { IMode } from "../../util/Navigation";

interface IProps {
  category?: ICategory;
  mode: IMode;
}

/**
 * Compare two ICartItems by a field of ICartItem.
 *
 * @param field - The field to compare.
 * @param isDecreasing - Whether the ordering should be increasing or decreasing; the default is false (so the order is increasing).
 *
 * @returns - A comparison function.
 */
const compareItemsBySubtraction = (field: keyof ICartItem, isDecreasing: boolean = false) => (firstItem: ICartItem, secondItem: ICartItem) => {
  if (isDecreasing) {
    const _temp = firstItem;
    firstItem = secondItem;
    secondItem = _temp;
  }
  // Regardless whether the order of the arguments is reversed, the subtraction operation is the same.
  return parseFloat(firstItem[field]) - parseFloat(secondItem[field]);
};

export const ProductsByCategory = ({ category, mode }: IProps): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const params = useParams<{ c: string; sc: string; p: string }>();
  const { p = "" } = params;
  const url = useLocation().pathname;

  const { items, products, product, view, loading } = useAppSelector(
    (state) => {
      return {
        items: itemsSelector(state),
        product: itemsSelector(state).find((o: ICartItem) => o.hash === p),
        products: category
          ? (state.product.list[category.title] || { rows: [] }).rows
          : [],
        loading: state.product.loading || state.category.loading,
        view: state.product.view,
      };
    }
  );

  let sortedProducts = [...products].sort(compareItemsBySubtraction("price"));  // The 'sort' method requires the array be mutable so 'products' is cloned.

  const categoryName = (category && category.title) || "";
  useEffect(() => {
    if (category) {
      fetchProductsByCategory(category.title)(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryName]);

  const onProduct = (doc: IProduct) => {
    if (doc) {
      const newElement = { ...doc, quantity: 1, url };
      if (doc.subcategories && doc.subcategories.length) {
        newElement.hash = genHash();
      } else {
        const existItem = items.find((o: ICartItem) => o._id === doc._id);
        if (existItem && existItem.hash) {
          newElement.hash = existItem.hash;
        } else {
          newElement.hash = genHash();
        }
      }
      const { hash, subcategories: categoryModifiers = [] } = newElement;
      const parent = url.split("/p/")[0];

      if (categoryModifiers && categoryModifiers.length) {
        navigate(`${parent}/p/${hash}/m`);
      } else {
        // if (!p) {
        newElement.url = parent.includes("/p") ? `${parent}` : `${parent}/p`;
        navigate(`${url || ""}`);
        // }
      }
      addOrderItem(newElement)(dispatch);
    }
  };

  const onProductModifier = (modifier: IProduct) => {
    if (modifier && modifier != null) {
      updateOrderModifier(product, modifier)(dispatch);
    }
  };
  const currentProduct = product
    ? items.find((o: ICartItem) => o.hash === product.hash)
    : null;

  return (
    <BrowserProductCards
      {...{
        loading,
        view,
        list: sortedProducts,
        items: currentProduct ? currentProduct.modifiers || [] : [],
        onSelect: mode !== "categoryModifier" ? onProduct : onProductModifier,
      }}
    />
  );
};
